import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { WhiteLabelService } from '@flexyoo/common';
import { EmployeeSelectedBenefit, EmployeeSelectedBenefitStatus } from '@flexyoo/employee/models';
import { employeeSelectedBenefitsSelectors } from '@flexyoo/employee/store';
import { shouldShowNetSavingColumn } from '@flexyoo/employee/util';
import { FlexyooNumberPipe } from '@flexyoo/shared/pipes';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { EmployeeBasketBenefitRowComponent } from '../employee-basket-benefit-row/employee-basket-benefit-row.component';

const CURRENT_BENEFIT_GROUP_ORDER: Exclude<EmployeeSelectedBenefitStatus, 'selection'>[] = [
  'confirmed',
  'resign',
  'reserved',
];

const GROUP_TITLE_MAP: Record<Exclude<EmployeeSelectedBenefitStatus, 'selection'>, string> = {
  confirmed: 'employee.basket.confirmed-benefits',
  resign: 'employee.basket.signature-required',
  reserved: 'employee.basket.reserved-benefits',
  cancelled: 'employee.basket.cancelled-benefits',
};

@Component({
  selector: 'flexyoo-employee-current-benefits',
  templateUrl: './employee-current-benefits.component.html',
  styleUrl: './employee-current-benefits.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    MatIconModule,
    MatExpansionModule,
    FlexyooNumberPipe,
    EmployeeBasketBenefitRowComponent,
  ],
})
export class EmployeeCurrentBenefitsComponent {
  private readonly store = inject(Store);

  readonly selectedBenefitId = input<number | null>(null);
  readonly signBenefit = output<EmployeeSelectedBenefit>();

  protected readonly wls = inject(WhiteLabelService);
  protected readonly expanded = signal(false);

  protected readonly totals = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectCurrentBenefitsTotals)
  );
  protected readonly statusGroups = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectCurrentBenefitsStatusGroups)
  );

  protected readonly groups = computed(() => {
    const statusGroups = this.statusGroups();
    if (!statusGroups) {
      return [];
    }

    return CURRENT_BENEFIT_GROUP_ORDER.filter((status) => statusGroups[status].length > 0).map(
      (status) => ({
        status,
        titleKey: GROUP_TITLE_MAP[status],
        benefits: statusGroups[status],
        color: this.getGroupColor(status),
        showWarning: status === 'resign',
      })
    );
  });

  protected readonly hasSignatureRequired = computed(
    () => (this.statusGroups()?.resign.length ?? 0) > 0
  );

  protected readonly showNetSavingColumn = computed(() =>
    shouldShowNetSavingColumn(this.groups().flatMap((group) => group.benefits))
  );

  private getGroupColor(status: Exclude<EmployeeSelectedBenefitStatus, 'selection'>): string {
    switch (status) {
      case 'confirmed':
        return this.wls.accentColor;
      case 'resign':
        return this.wls.warnColor;
      case 'reserved':
        return this.wls.primaryColor;
    }
  }
}
